import { useEffect, useRef } from 'react';
import { Divider, Input, Select, Typography, message } from 'antd';
import { MailOutlined, PaperClipOutlined } from '@ant-design/icons';
import TipTapEditor from '../TipTapEditor';
import { getRuntimeEmailTemplate } from '../../services/emailTemplateApi';
import { getModuleDocuments } from '../../services/documentApi';
import { parseEmails } from './mailUtils';

const { Text } = Typography;

const DOCUMENT_TYPES = new Set(['file', 'document', 'attachment']);

function isDocumentHolder(holder) {
  return DOCUMENT_TYPES.has(String(holder?.type ?? '').trim().toLowerCase());
}

function normalizeModuleName(value) {
  return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function objectIdValue(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return String(value?._id ?? value?.id ?? value?.$oid ?? '');
  return String(value);
}

function resolveModuleRecordId(record, sourceModule, currentModule) {
  if (!record) return '';
  const source = normalizeModuleName(sourceModule);
  const current = normalizeModuleName(currentModule);
  if (!source || source === current || source.replace(/s$/, '') === current.replace(/s$/, '')) {
    return objectIdValue(record?._id ?? record?.id);
  }
  const singular = source.replace(/s$/, '');
  const candidates = [
    `${singular}Id`, `${source}Id`, singular, source,
    `${singular}._id`, `${source}._id`,
  ];
  for (const path of candidates) {
    const value = nestedValue(record, path);
    const id = objectIdValue(value);
    if (id) return id;
  }
  return '';
}

async function fetchDocumentFile(doc) {
  const url = doc?.fileUrl ?? doc?.url ?? doc?.downloadUrl;
  if (!url) return null;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Unable to load attachment ${doc?.fileName || ''}`.trim());
  const blob = await response.blob();
  const name = doc?.fileName ?? doc?.uploadedFileName ?? doc?.uniqueName ?? 'attachment';
  return new File([blob], name, { type: doc?.contentType || blob.type || 'application/octet-stream' });
}

async function resolveTemplateAttachments(template, record, currentModule) {
  if (!record) return [];
  const tasks = [];
  (template?.placeholders ?? []).forEach((group) => {
    (group?.holders ?? []).filter(isDocumentHolder).forEach((holder) => {
      const refId = resolveModuleRecordId(record, group?.moduleName, currentModule);
      if (!refId) return;
      tasks.push((async () => {
        const result = await getModuleDocuments(group.moduleName, refId, holder.value);
        return Promise.all((result?.data ?? []).map(fetchDocumentFile));
      })());
    });
  });
  const files = (await Promise.all(tasks)).flat().filter(Boolean);
  const seen = new Set();
  return files.filter((file) => {
    const key = `${file.name}:${file.size}:${file.type}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function mergeEmails(current, incoming) {
  return [...new Set([...parseEmails(current), ...parseEmails(incoming)])].join(', ');
}

function mergeAttachments(current = [], incoming = []) {
  const seen = new Set();
  return [...current, ...incoming].filter((file) => {
    if (!file) return false;
    const key = `${file.name}:${file.size}:${file.type}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function nestedValue(record, path, arrayMode = '') {
  const mode = String(arrayMode ?? '').trim().toLowerCase();
  return String(path ?? '').split('.').reduce((value, key) => {
    let current = value;
    // A historical field such as submissions.proposedRate is an array.
    // arrayMode=last means the newest APPENDED snapshot, exactly as configured
    // by the template placeholder metadata.
    if (Array.isArray(current) && mode === 'last') current = current.at(-1);
    return current?.[key];
  }, record);
}

export function resolveTemplateText(text, placeholders, record) {
  if (!record) return String(text ?? '');
  const values = new Map();
  (placeholders ?? []).forEach((group) => (group.holders ?? []).forEach((holder) => {
    values.set(holder.key, isDocumentHolder(holder) ? '' : nestedValue(record, holder.value, holder.arrayMode));
  }));
  // Support both current {{.field}} and legacy {{field}} syntax.
  return String(text ?? '').replace(/{{\.?([\w.]+)}}/g, (token, key) => {
    const value = values.get(key);
    return value === undefined || value === null ? token : String(value);
  });
}

export default function CommonMailComposer({
  value,
  onChange,
  defaultTemplateId = '',
  ready = true,
  title = 'Mail',
  hint,
  bccHint,
  afterBcc,
  beforeFields,
  className = '',
  contextRecords = [],
  moduleName = '',
}) {
  const appliedDefault = useRef('');

  const patch = (next) => onChange?.({ ...value, ...next });

  async function applyTemplate(id, quiet = false) {
    if (!id) return;
    try {
      const template = await getRuntimeEmailTemplate(id);
      const record = contextRecords[0];
      const attachments = await resolveTemplateAttachments(template, record, moduleName);
      patch({
        to: mergeEmails(value.to, (template?.toMail ?? []).map((email) => resolveTemplateText(email, template?.placeholders, record))),
        cc: mergeEmails(value.cc, (template?.ccMail ?? []).map((email) => resolveTemplateText(email, template?.placeholders, record))),
        bcc: mergeEmails(value.bcc, (template?.bccMail ?? []).map((email) => resolveTemplateText(email, template?.placeholders, record))),
        subject: resolveTemplateText(template?.subject, template?.placeholders, record),
        contents: resolveTemplateText(template?.body, template?.placeholders, record),
        attachments: mergeAttachments(value.attachments, attachments),
      });
      if (!quiet) message.success(`Loaded ${template?.name || 'email template'}`);
    } catch (error) {
      if (!quiet) message.error(error?.message || 'Could not load email template');
    }
  }

  useEffect(() => {
    if (!ready || !defaultTemplateId || appliedDefault.current === defaultTemplateId) return;
    appliedDefault.current = defaultTemplateId;
    applyTemplate(defaultTemplateId, true);
    // Apply once after the popup's record defaults have loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTemplateId, ready]);

  return (
    <div className={`common-mail-composer ${className}`.trim()}>
      <div className="mail-popup__header">
        <div className="mail-popup__header-brand">
          <span className="dynamic-popup__header-icon-box">
            <MailOutlined />
          </span>
          <div>
            <span className="dynamic-popup__header-label">{title}</span>
            {hint && <Text type="secondary" className="mail-popup__header-hint">{hint}</Text>}
          </div>
        </div>
      </div>

      <div className="mail-popup__body">
        {beforeFields && <>{beforeFields}<Divider /></>}
        <div className="mail-popup__field-row">
          <Text type="secondary" className="mail-popup__field-label">To</Text>
          <Select mode="tags" open={false} tokenSeparators={[',']} value={parseEmails(value.to)} onChange={(items) => patch({ to: items.join(', ') })} variant="borderless" className="mail-popup__recipient-select" placeholder="Add email addresses" />
          <div className="mail-popup__cc-bcc-actions">
            {!value.showCc && <span className="mail-popup__cc-bcc-link" onClick={() => patch({ showCc: true })}>Cc</span>}
            {!value.showBcc && <span className="mail-popup__cc-bcc-link" onClick={() => patch({ showBcc: true })}>Bcc</span>}
          </div>
        </div>
        <Divider />
        {value.showCc && <><div className="mail-popup__field-row"><Text type="secondary" className="mail-popup__field-label">Cc</Text><Select mode="tags" open={false} tokenSeparators={[',']} value={parseEmails(value.cc)} onChange={(items) => patch({ cc: items.join(', ') })} variant="borderless" className="mail-popup__recipient-select" /></div><Divider /></>}
        {value.showBcc && <><div className="mail-popup__field-row"><Text type="secondary" className="mail-popup__field-label">Bcc</Text><Select mode="tags" open={false} tokenSeparators={[',']} value={parseEmails(value.bcc)} onChange={(items) => patch({ bcc: items.join(', ') })} variant="borderless" className="mail-popup__recipient-select" placeholder={bccHint} /></div>{afterBcc}<Divider /></>}
        <div className="mail-popup__field-row"><Text type="secondary" className="mail-popup__field-label">Subject</Text><Input variant="borderless" value={value.subject} onChange={(event) => patch({ subject: event.target.value })} placeholder="Enter subject" className="mail-popup__field-input" /></div>
        <Divider />
        {!!value.attachments?.length && (
          <>
            <div className="mail-popup__field-row">
              <Text type="secondary" className="mail-popup__field-label">Attachments</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0' }}>
                {value.attachments.map((file, index) => (
                  <span key={`${file.name}:${file.size}:${index}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <PaperClipOutlined />{file.name}
                  </span>
                ))}
              </div>
            </div>
            <Divider />
          </>
        )}
        <div className="mail-popup__message-section"><Text type="secondary" className="mail-popup__message-label">Message</Text><TipTapEditor value={value.contents} onChange={(contents) => patch({ contents })}/></div>
      </div>
    </div>
  );
}
